import { create } from 'zustand';
import type { GeoJSONFeature } from '@/types/map';
import type { TripRouteResponse, TripPositionResponse } from '@/types/route';
import { POI_TYPES } from '@/utils/constants';

interface MapState {
  vehicleFeatures: GeoJSONFeature[];
  routeLines: GeoJSONFeature[];
  riskZones: GeoJSONFeature[];
  poiMarkers: GeoJSONFeature[];
  searchAreas: GeoJSONFeature[];
  routeData: TripRouteResponse | null;
  livePosition: TripPositionResponse | null;
  highlightPosition: [number, number] | null;
  highwaysVisible: boolean;

  setFeatures: (features: GeoJSONFeature[]) => void;
  setRouteData: (data: TripRouteResponse | null) => void;
  setLivePosition: (pos: TripPositionResponse | null) => void;
  syncVehicleFeature: (alias: string, patch: Record<string, unknown>) => void;
  setHighlightPosition: (pos: [number, number] | null) => void;
  toggleHighways: () => void;
  clearRouteLines: () => void;
  clearFeatures: () => void;
}

// Mexico bounding box (with margin for border areas)
const MX_BOUNDS = { latMin: 14.0, latMax: 33.0, lonMin: -118.5, lonMax: -86.0 };

function isInMexico(lon: number, lat: number): boolean {
  return lat >= MX_BOUNDS.latMin && lat <= MX_BOUNDS.latMax
    && lon >= MX_BOUNDS.lonMin && lon <= MX_BOUNDS.lonMax;
}

function isValidCoordinatePair(lon: number, lat: number): boolean {
  return Number.isFinite(lon)
    && Number.isFinite(lat)
    && Math.abs(lon) <= 180
    && Math.abs(lat) <= 90
    && Math.abs(lon) > 0.0001
    && Math.abs(lat) > 0.0001;
}

function firstNonEmptyIdentity(...values: unknown[]): string | null {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

export const useMapStore = create<MapState>((set) => ({
  vehicleFeatures: [],
  routeLines: [],
  riskZones: [],
  poiMarkers: [],
  searchAreas: [],
  routeData: null,
  livePosition: null,
  highlightPosition: null,
  highwaysVisible: false,

  setFeatures: (features) => {
    const vehicleFeatures: GeoJSONFeature[] = [];
    const routeLines: GeoJSONFeature[] = [];
    const riskZones: GeoJSONFeature[] = [];
    const poiMarkers: GeoJSONFeature[] = [];
    const searchAreas: GeoJSONFeature[] = [];
    const seenVehicleIds = new Set<string>();

    for (const f of features) {
      const geomType = f.geometry.type;
      const props = f.properties;

      if (geomType === 'LineString') {
        routeLines.push(f);
      } else if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
        riskZones.push(f);
      } else if (geomType === 'Point') {
        const [lon, lat] = (f.geometry as { coordinates: number[] }).coordinates;
        if (props.radius_km) {
          searchAreas.push(f);
        } else if (POI_TYPES.includes(props.type as typeof POI_TYPES[number])) {
          poiMarkers.push(f);
        } else if (
          isValidCoordinatePair(lon, lat)
          && (props.allow_outside_mx === true || isInMexico(lon, lat))
        ) {
          const uniqueKey = firstNonEmptyIdentity(
            props.id_bitacora,
            props.id,
            props.alias,
          ) ?? `${lon},${lat}`;
          if (seenVehicleIds.has(uniqueKey)) {
            continue;
          }
          seenVehicleIds.add(uniqueKey);
          vehicleFeatures.push(f);
        }
      }
    }

    set({ vehicleFeatures, routeLines, riskZones, poiMarkers, searchAreas });
  },

  setRouteData: (data) => set({ routeData: data }),
  setLivePosition: (pos) => set({ livePosition: pos }),
  syncVehicleFeature: (alias, patch) => set((state) => {
    const normalizedAlias = alias.trim();
    if (!normalizedAlias || state.vehicleFeatures.length === 0) return state;

    let didUpdate = false;
    const vehicleFeatures = state.vehicleFeatures.map((feature) => {
      if (String(feature.properties.alias ?? '').trim() !== normalizedAlias) {
        return feature;
      }
      didUpdate = true;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          ...patch,
        },
      };
    });

    return didUpdate ? { vehicleFeatures } : state;
  }),
  setHighlightPosition: (pos) => set({ highlightPosition: pos }),
  toggleHighways: () => set((state) => ({ highwaysVisible: !state.highwaysVisible })),
  clearRouteLines: () => set({ routeLines: [] }),
  clearFeatures: () =>
    set({
      vehicleFeatures: [],
      routeLines: [],
      riskZones: [],
      poiMarkers: [],
      searchAreas: [],
    }),
}));
